import { RequireRole, useSessionTokenGetter } from '@develio/auth/client';
import { useCallback, useEffect, useState } from 'react';

/**
 * Settings → Governance — ADR-029 Phase 3b.
 *
 * Owner-only. Lists `authorization_records` and lets the owner record new
 * signed authorizations (profile_change, vendor_setup, consultant_override).
 * An active `profile_change` record is required before the API accepts any
 * mutation of methodology or jurisdiction profiles once an org default exists.
 *
 * Document upload: for now we accept a URL to the signed PDF (e.g. Dropbox /
 * S3 link). A hardened direct-upload flow ships with the file-storage plugin.
 */

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type Kind = 'vendor_setup' | 'profile_change' | 'consultant_override';
type ProfileType = 'jurisdiction' | 'assumption';

type AuthRecord = {
  id: string;
  kind: Kind;
  signerName: string;
  signerTitle: string;
  documentUrl: string;
  signedAt: string;
  activatedAt: string;
  revokedAt: string | null;
  scope: Record<string, unknown>;
  createdAt: string;
};

const KIND_LABELS: Record<Kind, string> = {
  vendor_setup: 'Vendor setup',
  profile_change: 'Profile change',
  consultant_override: 'Consultant override',
};

const AUTH_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000;

function isActive(r: AuthRecord): boolean {
  if (r.revokedAt) return false;
  const signedMs = Date.parse(r.signedAt);
  if (!Number.isFinite(signedMs)) return false;
  return Date.now() - signedMs <= AUTH_EXPIRY_MS;
}

function GovernancePanel() {
  const getToken = useSessionTokenGetter();
  const [records, setRecords] = useState<AuthRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const [kind, setKind] = useState<Kind>('profile_change');
  const [profileType, setProfileType] = useState<ProfileType>('assumption');
  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [documentUrl, setDocumentUrl] = useState('');
  const [signedAt, setSignedAt] = useState(new Date().toISOString().slice(0, 10));
  const [scopeNotes, setScopeNotes] = useState('');

  const fetchRecords = useCallback(async () => {
    const token = await getToken();
    const res = await fetch(`${API}/api/v1/authorization-records`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    const json = await res.json();
    setRecords(json.records as AuthRecord[]);
  }, [getToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await fetchRecords();
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchRecords]);

  function resetForm() {
    setKind('profile_change');
    setProfileType('assumption');
    setSignerName('');
    setSignerTitle('');
    setDocumentUrl('');
    setSignedAt(new Date().toISOString().slice(0, 10));
    setScopeNotes('');
  }

  async function handleSave() {
    if (!signerName || !signerTitle || !documentUrl || !signedAt) {
      setError('All fields except notes are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const scope: Record<string, unknown> =
        kind === 'profile_change'
          ? { profileType, notes: scopeNotes || undefined }
          : kind === 'consultant_override'
            ? { notes: scopeNotes || undefined }
            : { notes: scopeNotes || undefined };
      const res = await fetch(`${API}/api/v1/authorization-records`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          kind,
          signerName,
          signerTitle,
          documentUrl,
          signedAt: new Date(signedAt).toISOString(),
          scope,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error((j as { error?: string }).error ?? `${res.status}`);
      }
      await fetchRecords();
      setShowForm(false);
      resetForm();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleRevoke(id: string) {
    if (!confirm('Revoke this authorization? This cannot be undone.')) return;
    const token = await getToken();
    await fetch(`${API}/api/v1/authorization-records/${id}/revoke`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    await fetchRecords();
  }

  const activeProfileChange = records.filter((r) => r.kind === 'profile_change' && isActive(r));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Governance Authorizations</h2>
          <p className="mt-1 text-sm text-slate-500">
            Signed authorization records required for methodology and jurisdiction profile changes.
          </p>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            + Record authorization
          </button>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm">
        <p className="font-medium text-slate-800">
          Active profile_change coverage:{' '}
          {activeProfileChange.length === 0 ? (
            <span className="text-amber-700">none</span>
          ) : (
            <span className="text-green-700">
              {activeProfileChange.length} active record
              {activeProfileChange.length === 1 ? '' : 's'}
            </span>
          )}
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Once an org has a default profile of a given type, further changes require an active
          (signed within 365 days, not revoked) <code>profile_change</code> record with a matching{' '}
          <code>scope.profileType</code>.
        </p>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {showForm && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-base font-semibold text-slate-800">Record authorization</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="kind" className="block text-xs font-medium text-slate-600">
                Authorization kind
              </label>
              <select
                id="kind"
                value={kind}
                onChange={(e) => setKind(e.target.value as Kind)}
                className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              >
                <option value="profile_change">Profile change (methodology / jurisdiction)</option>
                <option value="vendor_setup">Vendor setup (initial configuration)</option>
                <option value="consultant_override">Consultant override</option>
              </select>
            </div>

            {kind === 'profile_change' && (
              <div>
                <label htmlFor="profileType" className="block text-xs font-medium text-slate-600">
                  Profile type covered
                </label>
                <select
                  id="profileType"
                  value={profileType}
                  onChange={(e) => setProfileType(e.target.value as ProfileType)}
                  className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                >
                  <option value="assumption">Methodology (Step 5 / 7 / 8)</option>
                  <option value="jurisdiction">Jurisdiction (Step 6)</option>
                </select>
              </div>
            )}

            <div>
              <label htmlFor="signerName" className="block text-xs font-medium text-slate-600">
                Signer name
              </label>
              <input
                id="signerName"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label htmlFor="signerTitle" className="block text-xs font-medium text-slate-600">
                Signer title
              </label>
              <input
                id="signerTitle"
                value={signerTitle}
                onChange={(e) => setSignerTitle(e.target.value)}
                className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div className="col-span-2">
              <label htmlFor="documentUrl" className="block text-xs font-medium text-slate-600">
                Document URL
              </label>
              <input
                id="documentUrl"
                value={documentUrl}
                onChange={(e) => setDocumentUrl(e.target.value)}
                placeholder="https://…/signed-authorization.pdf"
                className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              />
              <p className="mt-0.5 text-xs text-slate-400">
                Link to the countersigned PDF. Direct upload lands in a follow-up.
              </p>
            </div>
            <div>
              <label htmlFor="signedAt" className="block text-xs font-medium text-slate-600">
                Signed date
              </label>
              <input
                id="signedAt"
                type="date"
                value={signedAt}
                onChange={(e) => setSignedAt(e.target.value)}
                className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              />
              <p className="mt-0.5 text-xs text-slate-400">Expires 365 days from this date.</p>
            </div>
            <div className="col-span-2">
              <label htmlFor="scopeNotes" className="block text-xs font-medium text-slate-600">
                Scope notes (optional)
              </label>
              <input
                id="scopeNotes"
                value={scopeNotes}
                onChange={(e) => setScopeNotes(e.target.value)}
                placeholder="e.g. Authorizes 2026 RIMS multiplier refresh"
                className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              />
            </div>
          </div>
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Record'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      {!loading && records.length === 0 && !showForm && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
          <p className="text-sm text-slate-500">No authorizations on file yet.</p>
          <p className="mt-1 text-xs text-slate-400">
            Record a vendor_setup authorization after your first profile, then a profile_change
            record whenever methodology needs to move.
          </p>
        </div>
      )}

      {!loading && records.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Kind</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Signer</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Signed</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Scope</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {records.map((r) => {
                const active = isActive(r);
                return (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{KIND_LABELS[r.kind]}</td>
                    <td className="px-4 py-3 text-slate-600">
                      <div>{r.signerName}</div>
                      <div className="text-xs text-slate-400">{r.signerTitle}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {new Date(r.signedAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {(r.scope as { profileType?: string }).profileType ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      {r.revokedAt ? (
                        <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                          Revoked
                        </span>
                      ) : active ? (
                        <span className="inline-flex rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                          Expired
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <a
                        href={r.documentUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="mr-3 text-xs text-slate-500 hover:text-slate-900 hover:underline"
                      >
                        View PDF
                      </a>
                      {!r.revokedAt && (
                        <button
                          type="button"
                          onClick={() => handleRevoke(r.id)}
                          className="text-xs text-red-500 hover:text-red-700 hover:underline"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function SettingsGovernanceRoute() {
  return (
    <RequireRole
      requiredRole="owner"
      fallback={
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Governance Authorizations</h2>
            <p className="mt-1 text-sm text-slate-500">
              Signed authorizations required for methodology changes.
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-amber-50 p-4 text-sm text-amber-900">
            Governance records are visible to Owners only.
          </div>
        </div>
      }
    >
      <GovernancePanel />
    </RequireRole>
  );
}
